import React, { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import {
   bonoBotState,
   setSelectedCharities,
   setCharitiesList,
   setSelectedCauses,
   setDonationAmount,
   setConversationStatus,
   setThreadId,
} from 'store/bonoBot/bonoBot.slice';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useSocketInstanceContext } from 'context/socketInstanceContext';
import { ChatMessageType } from 'store/bonoBot/types';
import { CharityType, CauseType } from 'store/causes/types';
import { routes } from 'routes/routes';
import { preloadImage } from 'utils/utils';
import MessageBubble from './MessageBubble';
import ChatLoader from './ChatLoader';
import WelcomeMessage from './WelcomeMessage';

const ChatMessages = () => {
   const dispatch = useAppDispatch();
   const navigate = useNavigate();
   const {
      messages,
      selectedCharities,
      charitiesList,
      causesList,
      status: reduxStatus,
      storedThreadId,
   } = useAppSelector(bonoBotState);
   const scrollContainerRef = useRef<HTMLDivElement>(null);
   const lastMessageMinHeight = useRef<string>('0px');
   const lastUserMessageRef = useRef<HTMLDivElement>(null);
   const { isWaitingForResponse, closeSocket } = useSocketInstanceContext();

   const saveOptionalCharities = (optCharities: CharityType[]) => {
      dispatch(setCharitiesList(optCharities));
      optCharities.forEach((ch) => {
         preloadImage(ch.image);
      });
   };

   const updateReduxStore = useCallback(
      (message: ChatMessageType) => {
         const { optionalCharities, causes, charities, status: messageStatus, text, examplesType, threadId } = message;

         if (threadId && threadId !== storedThreadId) {
            dispatch(setThreadId(threadId));
         }

         if (optionalCharities && optionalCharities.length > 0) {
            console.log('setting charities list from optionalCharities:', optionalCharities);
            saveOptionalCharities(optionalCharities);
         }

         if (causes && causes.length > 0) {
            console.log('selecting cause');
            const causesToSelect: CauseType[] = [];
            causes.forEach((causeId) => {
               const cause = causesList.find((c) => c.id === causeId);
               if (cause) {
                  causesToSelect.push(cause);
               }
            });
            dispatch(setSelectedCauses(causesToSelect));
         }
         if (charities && charities.length > 0) {
            console.log('selecting charity');
            const selectedCharitiesIds = selectedCharities.map((ch) => ch.id);
            const charitiesToSelect = charitiesList.filter(
               (ch) => charities.includes(ch.id) && !selectedCharitiesIds.includes(ch.id)
            );
            dispatch(setSelectedCharities([...selectedCharities, ...charitiesToSelect]));
         }
         if (messageStatus && messageStatus !== reduxStatus) {
            dispatch(setConversationStatus(messageStatus));
         }
         if (messageStatus === 'payment_ready' && examplesType === 'general') {
            const dollarIndex = text.indexOf('$');
            const slashIndex = text.indexOf('/');
            if (dollarIndex !== -1) {
               const amount = text.substring(dollarIndex + 1, slashIndex);
               console.log({ amount });
               const isNumeric = !isNaN(Number(amount));
               const amountNumber = isNumeric ? Number(amount) : 20;
               if (amountNumber > 0) {
                  dispatch(setDonationAmount(amountNumber));
                  closeSocket();
                  navigate(routes().bonoBot.summary);
               }
            }
         }
      },
      [messages.length]
   );

   useEffect(() => {
      const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null;
      const isLastMessageFromUser = lastMessage && lastMessage.role === 'user';

      if (isLastMessageFromUser && lastUserMessageRef.current?.offsetHeight) {
         lastMessageMinHeight.current = `calc(100vh - ${lastUserMessageRef.current.offsetHeight + 284}px)`;
         if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTo({
               top: scrollContainerRef.current.scrollHeight,
               behavior: 'smooth',
            });
         }
      }
   }, [messages]);

   useEffect(() => {
      if (messages.length > 0 && messages[messages.length - 1].role === 'bot') {
         const lastMessage = messages[messages.length - 1];
         updateReduxStore(lastMessage);
      }
   }, [messages.length, updateReduxStore]);

   return (
      <Box
         ref={scrollContainerRef}
         sx={{
            // flex: 1,
            width: '100%',
            height: '100%',
            backgroundColor: '#F7F7F7',
            display: 'flex',
            flexDirection: 'column',
            px: { xs: 2.5, sm: 0 },
            overflowY: 'auto',
            scrollBehavior: 'smooth',
         }}
      >
         {messages.length === 0 && <WelcomeMessage />}
         {messages.map((m, i) => {
            const isLastMessage = i === messages.length - 1;
            let messageRef;
            if (m.role === 'user' && isLastMessage) {
               messageRef = lastUserMessageRef;
            }

            return (
               <MessageBubble
                  key={`chat-message-${i + 1}`}
                  ref={messageRef}
                  minHeight={lastMessageMinHeight.current}
                  role={m.role}
                  text={m.text}
                  examplesType={m.examplesType}
                  examples={m.examples}
                  isLastMessage={isLastMessage}
                  status={m.status}
               />
            );
         })}
         {isWaitingForResponse && <ChatLoader minHeight={lastMessageMinHeight.current} />}
      </Box>
   );
};

export default ChatMessages;
